<template>
  <div class="space-y-4">
    <select v-model="selectedValue" @change="onChange" class="select select-bordered w-full">
      <!-- placeholder always lives here -->
      <option disabled value="">
        {{ t("ui.inputs.pick_an_option") }}
      </option>

      <option v-for="option in options" :key="option.value" :value="option.value">
        {{ option.label }}
      </option>
    </select>

    <div v-if="showSummary && selectedOption" class="mt-4 p-3 border rounded bg-base-100">
      <h3 class="text-sm font-semibold text-base-content/70 mb-1">{{ t("ui.inputs.selected_option") }}</h3>
      <p class="text-base-content">{{ selectedOption.label }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Option {
  label: string;
  value: string;
}

const props = defineProps<{
  modelValue: string | null;
  options: Option[];
  showSummary?: boolean;
}>();
const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

// 1) initialize to the passed-in value, or "" for the placeholder
const selectedValue = ref<string>(props.modelValue ?? "");

// 2) when the parent *changes* the prop, mirror it (but don't override to a real option)
watch(
  () => props.modelValue,
  val => {
    selectedValue.value = val ?? "";
  }
);

// 3) when the user *actually picks* something, emit it
function onChange() {
  emit("update:modelValue", selectedValue.value);
}

const selectedOption = computed(() => props.options.find(o => o.value === selectedValue.value) ?? null);
</script>
